<template>
  <LazyHomeCommonLayout :scroll-class="scrollClassText" :is-main="true">
    <template #content>
      <section v-if="isOnLoad" class="settingWrap org">
        <div class="containerDash">
          <div class="settingArea flex">
            <LazyUserSettingsCommonMenu
                :tab="tab"
                :is-admin="isAdmin"
                :is-org-settings="true"
                :is-having-org="userStore.userV2.orgInfo !== null"
            />
            <div class="right">
              <LazyUserSettingsOrgInfo v-if="tab === UserOrgSettingsMenuType.ORGANIZATION"/>
              <LazyUserSettingsOrgPayment v-else-if="tab === UserOrgSettingsMenuType.PAYMENT"/>
              <LazyUserSettingsCreditManagement v-else-if="tab === UserOrgSettingsMenuType.CreditManagement"/>
              <LazyUserSettingsOrgMembersManage v-else />
            </div>
          </div>
        </div>
      </section>
    </template>
  </LazyHomeCommonLayout>
</template>

<script setup lang="ts">
import {LazyHomeCommonLayout} from '#components';
import {useLoadingStore} from '~/store/loading/loadingStore';
import {ref} from '#imports';
import {useUserStore} from '~/store/user/userStore';
import {computed} from 'vue';
import {UserOrgSettingsMenuType} from '~/components/User/Settings/Common/model/UserOrgSettingsMenuType';
import {useUtilsStore} from '~/store/utils/utilsStore';
import {useToastStore} from '~/store/toast/toastStore';
import {ToastType} from '~/components/Common/Toast/model/ToastType';
import {useCreditService, triggerCreditHistoryRefresh} from '~/composables/service/useCreditService';
import {useUserService} from '~/composables/service/useUserService';

const route = useRoute();
const router = useRouter();
const tab = ref<UserOrgSettingsMenuType>(route.query.tab as UserOrgSettingsMenuType);

const userStore = useUserStore();
const utilsStore = useUtilsStore();
const loadingStore = useLoadingStore();
const toastStore = useToastStore();
const { t } = useI18n();
const { guard } = usePermissionGuard();

const isOnLoad = ref(false);

const lastScrollY = ref(0);
const scrollClass = ref({hidden: false, on: false});

const isAdmin = computed(() => {
  return userStore.isAdminType || userStore.isSuperAdminType;
});

const setScrollEvent = () => {
  window.addEventListener("scroll", () => {
    const currentScrollY = window.scrollY;
    scrollClass.value.hidden = currentScrollY > lastScrollY.value && currentScrollY > 100;
    scrollClass.value.on = currentScrollY > 0;
    lastScrollY.value = currentScrollY;
  });
}

const handleRegisterInfo = () => {
  if (userStore.isLogin()) {
    if (!userStore.userV2.location) router.replace({path: "/register/info"});
    else if(utilsStore.isBlockedEmailDomain(userStore.userV2.email) && userStore.userV2.orgInfo === null)
      router.replace({ path: "/dashboard"} )
    else {
      loadingStore.closeLoading();
      isOnLoad.value = !loadingStore.show;
    }
  } else {
    router.replace({path: "/"});
  }
}

const scrollClassText = computed(() => {
  let text = "";
  if (scrollClass.value.hidden) text += "hidden ";
  if (scrollClass.value.on) text += "on ";
  return text;
});

const handlerOrgSettingMenuDoNotHavePermission = (): boolean => {
  return guard(
      !utilsStore.isBlockedEmailDomain(userStore.userV2.email),
      { redirectTo: { name: "dashboard" } }
  );
};

const handleTossCreditReturn = async () => {
  if (route.query.tossCredit !== '1') return;

  const cleanQuery = { tab: route.query.tab };

  if (route.query.paymentKey) {
    router.replace({ query: cleanQuery });
    try {
      await useCreditService().purchaseCredits({
        orderId: route.query.orderId as string,
        paymentKey: route.query.paymentKey as string,
        creditCount: Number(route.query.creditCount),
        couponCode: ''
      });
      await useUserService().setUser();
      triggerCreditHistoryRefresh();
      toastStore.openToast({ toastType: ToastType.SUCCESS, contents: t('credit-history-top-up-credits-success') });
    } catch {
      toastStore.openToast({ toastType: ToastType.FAILED, contents: t('credit-history-top-up-credits-fail') });
    }
  } else if (route.query.code) {
    router.replace({ query: cleanQuery });
    if (route.query.code !== 'PAY_PROCESS_CANCELED') {
      toastStore.openToast({ toastType: ToastType.FAILED, contents: t('credit-history-top-up-credits-fail') });
    }
  }
};

onMounted(async () => {
  // The permission guard reads isBlockedEmailDomain() synchronously; ensure the
  // list is loaded first so a hard load doesn't evaluate it against an empty
  // (still-fetching) cache and skip the redirect for a blocked-email user.
  await utilsStore.fetchBlockedEmailDomains();
  if (!handlerOrgSettingMenuDoNotHavePermission()) return;

  lastScrollY.value = window.scrollY;
  setScrollEvent();
  handleRegisterInfo();

  if (route.query.notify === 'payment-method-success') {
    toastStore.openToast({
      toastType: ToastType.SUCCESS,
      contents: t('organization-subscription-change-payment-method-success'),
    })
    router.replace({ query: { ...route.query, notify: undefined } })
  }

  await handleTossCreditReturn();
});

watch(() => route.query.tab, () => {
  if (route.query.tab) tab.value = route.query.tab as UserOrgSettingsMenuType;
  else tab.value = UserOrgSettingsMenuType.ORGANIZATION;
}, { immediate: true });
</script>

<style>
@import "assets/scss/setting_style.scss";
</style>